import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert
} from '@mui/material';
import DashboardLayout from '../../../../components/patient/layouts/DashboardLayout';
import { useDispatch } from 'react-redux';

// Mock data - replace with actual API call
const mockPurchases = [
  {
    id: 1,
    medicationName: 'Amoxicillin',
    quantity: 30,
    purchaseDate: '2023-01-15',
    price: 25.99,
    orderId: 'ORD-001'
  },
  {
    id: 2,
    medicationName: 'Lisinopril',
    quantity: 90,
    purchaseDate: '2023-02-20',
    price: 45.5,
    orderId: 'ORD-002'
  },
  {
    id: 3,
    medicationName: 'Metformin',
    quantity: 60,
    purchaseDate: '2023-03-10',
    price: 32.75,
    orderId: 'ORD-003'
  },
  {
    id: 4,
    medicationName: 'Atorvastatin',
    quantity: 30,
    purchaseDate: '2023-04-05',
    price: 55.25,
    orderId: 'ORD-004'
  },
  {
    id: 5,
    medicationName: 'Albuterol Inhaler',
    quantity: 1,
    purchaseDate: '2023-05-12',
    price: 65.0,
    orderId: 'ORD-005'
  },
  {
    id: 6,
    medicationName: 'Levothyroxine',
    quantity: 90,
    purchaseDate: '2023-06-18',
    price: 28.5,
    orderId: 'ORD-006'
  },
  {
    id: 7,
    medicationName: 'Omeprazole',
    quantity: 30,
    purchaseDate: '2023-07-22',
    price: 19.99,
    orderId: 'ORD-007'
  },
  {
    id: 8,
    medicationName: 'Sertraline',
    quantity: 60,
    purchaseDate: '2023-08-30',
    price: 42.25,
    orderId: 'ORD-008'
  }
];

const MedicalPurchasesPage = () => {
  const dispatch = useDispatch();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    // Simulate API call
    const fetchPurchases = async () => {
      try {
        // Replace with actual API call
        // const response = await fetch('/api/medical_purchases');
        // const data = await response.json();

        // Using mock data for now
        setTimeout(() => {
          setPurchases(mockPurchases);
          setLoading(false);
        }, 1000);
      } catch (err) {
        setError('Failed to fetch medical purchases. Please try again later.');
        setLoading(false);
      }
    };

    fetchPurchases();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={css(styles.container)}
    >
      <Typography variant='h4' className={css(styles.title)}>
        Medical Purchases History
      </Typography>

      <Paper elevation={3} className={css(styles.contentPaper)}>
        <Typography variant='body1' className={css(styles.description)}>
          View your medication purchase history. This information is
          automatically imported from your orders and cannot be modified.
        </Typography>

        {loading
          ? (
            <div className={css(styles.loadingContainer)}>
              <CircularProgress color='primary' />
              <Typography variant='body2'>
                Loading your purchase history...
              </Typography>
            </div>
            )
          : error
            ? (
              <Alert severity='error' className={css(styles.alert)}>
                {error}
              </Alert>
              )
            : purchases.length === 0
              ? (
                <Alert severity='info' className={css(styles.alert)}>
                  No purchase history found. Your medication purchases will appear
                  here once you make an order.
                </Alert>
                )
              : (
                <>
                  <TableContainer className={css(styles.tableContainer)}>
                    <Table stickyHeader aria-label='medical purchases table'>
                      <TableHead>
                        <TableRow>
                          <TableCell className={css(styles.tableHeader)}>
                            Medication
                          </TableCell>
                          <TableCell className={css(styles.tableHeader)}>
                            Quantity
                          </TableCell>
                          <TableCell className={css(styles.tableHeader)}>
                            Purchase Date
                          </TableCell>
                          <TableCell className={css(styles.tableHeader)}>
                            Price
                          </TableCell>
                          <TableCell className={css(styles.tableHeader)}>
                            Order ID
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {purchases
									  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									  .map((purchase) => (
  <TableRow key={purchase.id} hover>
    <TableCell className={css(styles.tableBodyTextColor)}>
      {purchase.medicationName}
    </TableCell>
    <TableCell className={css(styles.tableBodyTextColor)}>
      {purchase.quantity}
    </TableCell>
    <TableCell className={css(styles.tableBodyTextColor)}>
      {formatDate(purchase.purchaseDate)}
    </TableCell>
    <TableCell className={css(styles.tableBodyTextColor)}>
      {formatPrice(purchase.price)}
    </TableCell>
    <TableCell className={css(styles.tableBodyTextColor)}>
      {purchase.orderId}
    </TableCell>
  </TableRow>
									  ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component='div'
                    count={purchases.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    className={css(styles.tableBodyTextColor)}
                  />
                </>
                )}
      </Paper>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  title: {
    color: '#2783F7',
    marginBottom: '1.5rem',
    fontWeight: 'bold',
    fontSize: '1.8rem',
    '@media (max-width: 600px)': {
      fontSize: '1.5rem'
    }
  },
  contentPaper: {
    padding: '1.5rem',
    borderRadius: '8px',
    backgroundColor: '#ffffff'
  },
  description: {
    marginBottom: '1.5rem',
    color: '#000000'
    // color: '#555',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 0',
    gap: '1rem'
  },
  alert: {
    marginBottom: '1rem'
  },
  tableContainer: {
    marginBottom: '1rem',
    maxHeight: '400px'
  },
  tableHeader: {
    backgroundColor: '#e6f0fd',
    fontWeight: 'bold',
    color: '#2783F7'
  },
  tableBodyTextColor: {
    color: '#000000'
  }
});

export default MedicalPurchasesPage;
